import React, { useEffect, useState } from "react";
import { useAppContext } from "../../share-components/AppContext";
import { useNavigate } from "react-router-dom";
import FormTitle from "../../share-components/FormTitle";
import SearchColumnSelect from "../../share-components/SearchColumnSelect";
import CustomTable from "../../share-components/CustomTable";
import {
    currentMonth, currentYear, customStorage, formatDateTime, getAvailableMonths, getChangingSelectColor, getPayloadData,
    handleFromMonthChange, handleFromYearChange, handleToMonthChange, handleToYearChange, isNullOrEmptyString, isNullOrUndefinedObj, years
} from "../../services/UtilityService";
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from "../../constants/commonConstants";
import { Button, Card } from "react-bootstrap";
import { getAllActiveUsers, getLastLockedYearMonth } from "../../services/CommonService";
import { PAGE_MONEY_ADJUSTMENT_LIST, ROUTE_EDIT_MONEY_ADJUSTMENT, ROUTE_MONEY_ADJUSTMENT_DETAILS, ROUTE_MONEY_ADJUSTMENT_LIST } from "../../constants/routeConstants";
import NoDataFound from "../../share-components/NoDataFound";
import useScrollRestoration from "../../share-components/UseScrollRestoration";
import { getMoneyAdjustments } from "../../services/MoneyAdjustmentService";

const MoneyAdjustmentList = () => {

    useScrollRestoration(PAGE_MONEY_ADJUSTMENT_LIST);

    const { loading, setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [adjustmentList, setAdjustmentList] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const navigate = useNavigate();

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const initialSearchDataObj = sessionStorage.getItem(ROUTE_MONEY_ADJUSTMENT_LIST);
    const initialSearchData = initialSearchDataObj ? JSON.parse(initialSearchDataObj) : null;

    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [fromYear, setFromYear] = useState(initialSearchData?.fromYear || currentYear);
    const [fromMonth, setFromMonth] = useState(initialSearchData?.fromMonth || currentMonth);
    const [toYear, setToYear] = useState(initialSearchData?.toYear || currentYear);
    const [toMonth, setToMonth] = useState(initialSearchData?.toMonth || currentMonth);
    const [paidById, setPaidById] = useState(initialSearchData?.paidById || "");
    const [acceptedById, setAcceptedById] = useState(initialSearchData?.acceptedById || "");
    const [afterGetDataStore, setAfterGetDataStore] = useState({
        fromYear: initialSearchData?.fromYear || currentYear,
        fromMonth: initialSearchData?.fromMonth || currentMonth,
        toYear: initialSearchData?.toYear || currentYear,
        toMonth: initialSearchData?.toMonth || currentMonth,
        paidById: initialSearchData?.paidById || "",
        acceptedById: initialSearchData?.acceptedById || ""
    });

    const [activeUsers, setActiveUsers] = useState([]);
    const [searchData, setSearchData] = useState(null);

    useEffect(() => {
        const searchData = {
            fromYear,
            fromMonth,
            toYear,
            toMonth,
            paidById,
            acceptedById
        };
        setSearchData(searchData);
    }, [loading]);

    const fetchActiveUsers = async () => {
        const response = await getAllActiveUsers();
        const sortedUsers = (response || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
        setActiveUsers(sortedUsers);
    }

    const fetchLastLockedDate = async () => {
        const lastLockedData = await getLastLockedYearMonth();
        setLastLockedYearMonth(lastLockedData || null);
    }


    const fetchAdjustmentList = async () => {
        try {
            setLoading(true);
            const formData = {
                paidById: paidById || null,
                acceptedById: acceptedById || null,
                fromYear: fromYear,
                fromMonth: fromMonth,
                toYear: toYear,
                toMonth: toMonth
            }
            const response = await getMoneyAdjustments(formData);
            const adjustments = (response?.moneyAdjustmentList || [])?.map(data => ({
                ...data,
                formatedPaymentDate: formatDateTime(data?.paymentDate?.slice(0, 8)),
                formattedFromToUser: `${data?.paidBy} => ${data?.acceptedBy}`
            }));
            setAdjustmentList(adjustments);
            const afterFetchStore = {
                fromYear,
                fromMonth,
                toYear,
                toMonth,
                paidById,
                acceptedById
            }
            setAfterGetDataStore(afterFetchStore);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchInitialLoad = async () => {
        try {
            setLoading(true);
            await fetchLastLockedDate();
            await fetchActiveUsers();
            await fetchAdjustmentList();
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRouteNaviData("MONEY ADJUSTMENT >> ADJUSTMENT LIST");
        fetchInitialLoad();
    }, []);

    const canValidProcess = (paymentDate, paidById) => {
        const dateStr = paymentDate || '';
        const paymentYear = dateStr.length >= 4 ? parseInt(dateStr.slice(0, 4), 10) : 0;
        const paymentMonth = dateStr.length >= 6 ? parseInt(dateStr.slice(4, 6), 10) : 0;
        const isValidUser = !isNullOrEmptyString(paidById) && paidById === currentUser?.userId;
        const isValidLockDate = isNullOrUndefinedObj(lastLockedYearMonth) || lastLockedYearMonth.toYear < paymentYear ||
            (lastLockedYearMonth.toYear === paymentYear && lastLockedYearMonth.toMonth < paymentMonth);
        return isValidUser && isValidLockDate;
    }

    const columns = [
        {
            Header: 'Payment Date',
            accessor: 'formatedPaymentDate',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'PaidBy => AcceptedBy',
            accessor: 'formattedFromToUser',
            style: {
                maxWidth: "300px",
                // fontWeight: "bold"
            }
        },
        {
            Header: 'Payment Amount(¥)',
            accessor: 'paymentAmount',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Edit',
            accessor: 'edit',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "70px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    {canValidProcess(row.original?.paymentDate, row.original?.paidById) ? <Button
                        variant="secondary"
                        style={{
                            padding: "0px 5px",
                            margin: "5px"
                        }}
                        onClick={() => navigate(`${ROUTE_EDIT_MONEY_ADJUSTMENT}/${row.original?.moneyAdjustmentId}`)}
                    >
                        <i className="bi bi-pencil-square"></i>
                        {/* <span>Edit</span> */}
                    </Button> :
                        <Button variant="danger" disabled
                            style={{
                                padding: "0px 5px",
                                margin: "5px"
                            }}>
                            <i className="bi bi-ban"></i>
                            {/* <span>Disabled</span> */}
                        </Button>
                    }
                </div>
            ),
        },
        {
            Header: 'Details',
            accessor: 'details',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "50px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    <Button
                        variant="secondary"
                        style={{
                            padding: "0px 5px",
                            margin: "5px"
                        }}
                        onClick={() => navigate(`${ROUTE_MONEY_ADJUSTMENT_DETAILS}/${row.original?.moneyAdjustmentId}`)}
                    >
                        <i className="bi bi-info-circle"></i>
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div id='moneyAdjustmentListPage'>
            <FormTitle title={"Adjustment List"} isCenter={true} />
            {isFirstLoaded && <>
                <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {afterGetDataStore?.fromYear}/{afterGetDataStore?.fromMonth} ～ {afterGetDataStore?.toYear}/{afterGetDataStore?.toMonth}
                </div>
                <div style={{ overflow: "auto", paddingBottom: "50px" }}>
                    <div style={{ minWidth: "max-content", maxWidth: "850px", margin: '0 auto' }}>
                        <Card>
                            <Card.Body style={{
                                padding: '10px 5px 20px 5px'
                            }}>
                                <div>
                                    <div>
                                        <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                            {/* From Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="fromYear" style={{ minWidth: "50px" }}>
                                                    From
                                                </label>
                                                <div>
                                                    <select
                                                        id="fromYear"
                                                        className={`customInputControl ${getChangingSelectColor(fromYear, afterGetDataStore?.fromYear)}`}
                                                        value={fromYear}
                                                        onChange={(e) => handleFromYearChange(e, fromMonth, toYear, toMonth, setFromYear, setFromMonth, setToYear, setToMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="fromMonth"
                                                        className={`customInputControl ${getChangingSelectColor(fromMonth, afterGetDataStore?.fromMonth)}`}
                                                        value={fromMonth}
                                                        onChange={(e) => handleFromMonthChange(e, fromYear, toYear, toMonth, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(fromYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* To Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="toYear" style={{ minWidth: "50px" }}>
                                                    To
                                                </label>
                                                <div>
                                                    <select
                                                        id="toYear"
                                                        className={`customInputControl ${getChangingSelectColor(toYear, afterGetDataStore?.toYear)}`}
                                                        value={toYear}
                                                        onChange={(e) => handleToYearChange(e, fromYear, fromMonth, toMonth, setFromYear, setToYear, setFromMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="toMonth"
                                                        className={`customInputControl ${getChangingSelectColor(toMonth, afterGetDataStore?.toMonth)}`}
                                                        value={toMonth}
                                                        onChange={(e) => handleToMonthChange(e, fromYear, fromMonth, toYear, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(toYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '5px', marginTop: '10px', display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>                                           
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="paidBy" style={{ minWidth: "50px" }}>
                                                    Paid By
                                                </label>
                                                <div>
                                                    <select
                                                        id="paidBy"
                                                        className={`customInputControl select-min-max-width ${getChangingSelectColor(paidById, afterGetDataStore?.paidById)}`}
                                                        value={paidById}
                                                        onChange={(e) => setPaidById(e.target.value || '')}
                                                    >
                                                        <option value="">--All--</option>
                                                        {activeUsers.map(user => (
                                                            <option key={user.userId} value={user.userId}>{user.username}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="acceptedBy" style={{ minWidth: "50px" }}>
                                                    Accepted By
                                                </label>
                                                <div>
                                                    <select
                                                        id="acceptedBy"
                                                        className={`customInputControl select-min-max-width ${getChangingSelectColor(acceptedById, afterGetDataStore?.acceptedById)}`}
                                                        value={acceptedById}
                                                        onChange={(e) => setAcceptedById(e.target.value || '')}
                                                    >
                                                        <option value="">--All--</option>
                                                        {activeUsers.map(user => (
                                                            <option key={user.userId} value={user.userId}>{user.username}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-inline">
                                            <button type='button' className="btn btn-success btn-sm appBtn"
                                                onClick={fetchAdjustmentList}
                                            >Get Data</button>
                                        </div>
                                        <div style={{ width: "max-content" }}>
                                            <div style={{ marginBottom: '10px', marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                                <div style={{ display: 'inline-block' }}>
                                                    <SearchColumnSelect data={adjustmentList} setData={setFilteredData} columns={columns}
                                                        storeSessionKey={ROUTE_MONEY_ADJUSTMENT_LIST}
                                                        sessionData={searchData} />
                                                </div>
                                            </div>
                                            {filteredData?.length > 0 ?
                                                <CustomTable
                                                    tableId='moneyAdjustmentListTbl'
                                                    columns={columns}
                                                    tblData={filteredData || []}
                                                    showPagination={true}
                                                    itemsPerPage={20}
                                                    initialSortBy={[{ id: 'formatedPaymentDate', desc: true }]}
                                                    isStorePageData={true}
                                                /> : <NoDataFound />}
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </>}
        </div>
    );
}

export default MoneyAdjustmentList;